import { CopyPlus, Plus, Trash2 } from "lucide-react";
import { DateTime } from "luxon";
import { useEffect, useMemo, useRef, useState } from "react";

import { useToast } from "@/app/providers/ToastProvider";
import { CurrencyInput } from "@/components/common/CurrencyInput";
import { FormErrorSummary } from "@/components/common/FormErrorSummary";
import { FormField } from "@/components/common/FormField";
import { LoadingOverlay } from "@/components/common/LoadingOverlay";
import { NumericStepper } from "@/components/common/NumericStepper";
import { SelectField } from "@/components/common/SelectField";
import { unitLabel } from "@/components/common/UnitPicker";
import { BackButton } from "@/components/ui/back-button";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/ui/page-header";
import { accountLabel } from "@/features/accounts/accountLabel";
import { useAccounts } from "@/features/accounts/hooks/useAccounts";
import { useCurrency } from "@/features/accounts/hooks/useCurrency";
import { useCreateProvisioning } from "@/features/provision-stocks/hooks/useProvisioning";
import { useProvisions } from "@/features/provisions/hooks/useProvisions";
import { getApiValidationErrors } from "@/lib/apiClient";
import { expiryInstant, purchaseInstant } from "@/lib/datetime";
import { currencySymbol, formatMoney, parseLocalizedNumber } from "@/lib/format";
import { focusFirstField } from "@/lib/forms";
import { toErrorMessage } from "@/lib/swr";
import { cn } from "@/lib/utils";
import type { ProvisionStockStateDto } from "@/types/stock.types";

type Line = {
  key: string;
  provisionId: string;
  quantity: string;
  price: string;
  expiryDate: string;
};

type LineErrors = {
  quantity?: string;
  price?: string;
  expiryDate?: string;
};

type StoredLine = Omit<Line, "key"> & { key?: string };

type ProvisioningDraft = {
  purchaseDate?: string;
  accountId?: string;
  label?: string;
  lines?: StoredLine[];
};

const DRAFT_VERSION = 1;

const emptyLine = (): Line => ({
  key: crypto.randomUUID(),
  provisionId: "",
  quantity: "",
  price: "",
  expiryDate: "",
});

/** Same metrics as Input, for the native date fields that stay native. */
const FIELD_CLASS =
  "h-12 w-full rounded-lg border border-input bg-card px-4 text-base outline-none focus:ring-2 focus:ring-ring";

const VALIDATION_MESSAGES: Record<string, string> = {
  "Expiry date must be in the future.": "La date de péremption doit être dans le futur.",
  "Purchase date cannot be in the future.": "La date d’achat ne peut pas être dans le futur.",
  "Provision date cannot be in the future.": "La date d’approvisionnement ne peut pas être dans le futur.",
};

function userValidationMessage(message: string): string {
  return VALIDATION_MESSAGES[message] ?? message;
}

function validationSummary(error: unknown): string | null {
  const messages = Object.values(getApiValidationErrors(error)).map(userValidationMessage);
  return messages.length > 0 ? [...new Set(messages)].join(" ") : null;
}

function isPositiveNumber(value: string): boolean {
  const parsed = parseLocalizedNumber(value);
  return Number.isFinite(parsed) && parsed > 0;
}

export default function ProvisioningPage({ stock, onBack }: { stock: ProvisionStockStateDto; onBack: () => void }) {
  const [purchaseDate, setPurchaseDate] = useState(DateTime.now().toISODate() ?? "");
  const [accountId, setAccountId] = useState("");
  const [label, setLabel] = useState("");
  const [lines, setLines] = useState<Line[]>([emptyLine()]);
  const [lineErrors, setLineErrors] = useState<Record<string, LineErrors>>({});
  const [purchaseDateError, setPurchaseDateError] = useState<string | undefined>();
  const [formError, setFormError] = useState<string>();
  const [draftRestored, setDraftRestored] = useState(false);
  const draftReady = useRef(false);
  const toast = useToast();

  /** The API rejects a purchase in the future and an expiry already past. */
  const today = DateTime.now().toISODate() ?? "";

  const { provisions } = useProvisions();
  const { accounts } = useAccounts();
  const { currency } = useCurrency();
  const { createBatch, isSubmitting } = useCreateProvisioning();

  const draftStorageKey = `nafa:provisioning-draft:v${DRAFT_VERSION}:${stock.id}`;

  useEffect(() => {
    if (draftReady.current || provisions.length === 0) return;

    draftReady.current = true;
    try {
      const rawDraft = window.localStorage.getItem(draftStorageKey);
      if (!rawDraft) return;

      const draft = JSON.parse(rawDraft) as ProvisioningDraft;
      const knownProvisionIds = new Set(provisions.map((provision) => provision.id));
      const restoredLines = Array.isArray(draft.lines)
        ? draft.lines
            .filter((line) => line && (!line.provisionId || knownProvisionIds.has(line.provisionId)))
            .map((line) => ({
              key: line.key || crypto.randomUUID(),
              provisionId: line.provisionId || "",
              quantity: line.quantity || "",
              price: line.price || "",
              expiryDate: line.expiryDate || "",
            }))
        : [];

      if (draft.purchaseDate) setPurchaseDate(draft.purchaseDate);
      if (typeof draft.accountId === "string") setAccountId(draft.accountId);
      if (typeof draft.label === "string") setLabel(draft.label);
      if (restoredLines.length > 0) setLines(restoredLines);
      setDraftRestored(true);
    } catch {
      window.localStorage.removeItem(draftStorageKey);
    }
  }, [draftStorageKey, provisions]);

  useEffect(() => {
    if (!draftReady.current) return;

    const hasDraftContent = Boolean(
      accountId ||
      label.trim() ||
      lines.some((line) => line.provisionId || line.quantity || line.price || line.expiryDate) ||
      purchaseDate !== today,
    );
    if (!hasDraftContent) {
      window.localStorage.removeItem(draftStorageKey);
      return;
    }

    window.localStorage.setItem(
      draftStorageKey,
      JSON.stringify({ purchaseDate, accountId, label, lines } satisfies ProvisioningDraft),
    );
  }, [accountId, draftStorageKey, label, lines, purchaseDate, today]);

  const selectedAccount = accountId || accounts[0]?.id || "";

  const selectedLines = useMemo(() => lines.filter((line) => line.provisionId), [lines]);
  const filled = useMemo(() => selectedLines.filter((line) => isPositiveNumber(line.quantity)), [selectedLines]);

  const total = useMemo(() => filled.reduce((sum, line) => sum + (parseLocalizedNumber(line.price) || 0), 0), [filled]);

  const updateLine = (key: string, patch: Partial<Line>) => {
    setLines((prev) => prev.map((line) => (line.key === key ? { ...line, ...patch } : line)));
    if (Object.keys(patch).length > 0) {
      setLineErrors((prev) => {
        if (!prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setFormError(undefined);
    }
  };

  const duplicateLastLine = () => {
    const source = lines[lines.length - 1];
    if (!source) return;
    const duplicatedKey = crypto.randomUUID();
    setLines((prev) => [
      ...prev,
      {
        ...source,
        key: duplicatedKey,
        quantity: "",
        price: "",
      },
    ]);
    setLineErrors((prev) => {
      const next = { ...prev };
      delete next[duplicatedKey];
      return next;
    });
    focusFirstField([`${duplicatedKey}-quantity`]);
  };

  const handleSubmit = async () => {
    setLineErrors({});
    setPurchaseDateError(undefined);
    setFormError(undefined);

    if (selectedLines.length === 0) {
      setFormError("Ajoutez au moins une matière avec sa quantité.");
      toast.error("Ajoutez au moins une matière avec sa quantité.");
      return;
    }
    if (!selectedAccount) {
      setFormError("Choisissez le compte qui a payé.");
      toast.error("Choisissez le compte qui a payé.");
      return;
    }

    const invalidQuantityLines = selectedLines.filter((line) => !isPositiveNumber(line.quantity));
    if (invalidQuantityLines.length > 0) {
      const firstInvalidLine = invalidQuantityLines[0];
      if (!firstInvalidLine) return;
      const message = "Indiquez une quantité supérieure à 0.";
      setLineErrors(Object.fromEntries(invalidQuantityLines.map((line) => [line.key, { quantity: message }])));
      setFormError(message);
      focusFirstField([`${firstInvalidLine.key}-quantity`]);
      toast.error("Indiquez une quantité supérieure à 0 pour chaque ligne.");
      return;
    }
    /** The API refuses a price of 0, so an untyped price is caught here. */
    const invalidPriceLines = selectedLines.filter((line) => !isPositiveNumber(line.price));
    if (invalidPriceLines.length > 0) {
      const firstInvalidLine = invalidPriceLines[0];
      if (!firstInvalidLine) return;
      const message = "Indiquez le prix payé.";
      setLineErrors(Object.fromEntries(invalidPriceLines.map((line) => [line.key, { price: message }])));
      setFormError(message);
      focusFirstField([`${firstInvalidLine.key}-price`]);
      toast.error("Indiquez le prix payé pour chaque ligne.");
      return;
    }

    const now = DateTime.now();
    const todayStart = now.startOf("day");
    const parsedPurchaseDate = DateTime.fromISO(purchaseDate);
    if (!parsedPurchaseDate.isValid || parsedPurchaseDate.startOf("day") > todayStart) {
      const message = "La date d’achat ne peut pas être ultérieure à aujourd’hui.";
      setPurchaseDateError(message);
      setFormError(message);
      focusFirstField(["provisioning-purchase-date"]);
      toast.error("La date d’achat ne peut pas être dans le futur.");
      return;
    }
    setPurchaseDateError(undefined);

    const invalidExpiryLines = selectedLines.filter((line) => {
      if (!line.expiryDate) return false;
      const parsedExpiryDate = DateTime.fromISO(line.expiryDate);
      return !parsedExpiryDate.isValid || parsedExpiryDate.startOf("day") < todayStart;
    });
    if (invalidExpiryLines.length > 0) {
      const firstInvalidLine = invalidExpiryLines[0];
      if (!firstInvalidLine) return;
      const message = "La date de péremption ne peut pas être antérieure à aujourd’hui.";
      const nextLineErrors = Object.fromEntries(invalidExpiryLines.map((line) => [line.key, { expiryDate: message }]));
      setLineErrors(nextLineErrors);
      setFormError(message);
      focusFirstField([`${firstInvalidLine.key}-expiry`]);
      toast.error("Choisissez une date de péremption égale ou postérieure à aujourd’hui.");
      return;
    }

    const provisionedAt = purchaseInstant(purchaseDate);
    setLineErrors({});

    try {
      await createBatch(
        filled.map((line) => ({
          provisionId: line.provisionId,
          stockId: stock.id,
          provisionedAt,
          purchaseDate: provisionedAt,
          expiryDate: line.expiryDate ? expiryInstant(line.expiryDate) : undefined,
          label: label.trim() || undefined,
          quantity: parseFloat(line.quantity),
          price: parseLocalizedNumber(line.price),
          accountId: selectedAccount,
        })),
      );
      window.localStorage.removeItem(draftStorageKey);
      setDraftRestored(false);
      toast.success(
        filled.length > 1
          ? `${filled.length} entrées enregistrées dans ${stock.name}.`
          : `Entrée enregistrée dans ${stock.name}.`,
      );
      onBack();
    } catch (e) {
      // The lines stay put so nothing has to be retyped.
      const validationErrors = getApiValidationErrors(e);
      const nextLineErrors: Record<string, LineErrors> = {};
      for (const [field, message] of Object.entries(validationErrors)) {
        const match = /^items\[(\d+)\]\.expiryDate$/.exec(field);
        const line = match ? filled[Number(match[1])] : undefined;
        if (line) nextLineErrors[line.key] = { expiryDate: userValidationMessage(message) };
      }
      setLineErrors(nextLineErrors);

      const message = validationSummary(e) ?? toErrorMessage(e) ?? "L’approvisionnement n’a pas pu être enregistré.";
      setFormError(message);
      focusFirstField(Object.keys(nextLineErrors).map((key) => `${key}-expiry`));
      toast.error(message);
    }
  };

  const clearDraft = () => {
    window.localStorage.removeItem(draftStorageKey);
    setPurchaseDate(DateTime.now().toISODate() ?? "");
    setAccountId("");
    setLabel("");
    setLines([emptyLine()]);
    setLineErrors({});
    setPurchaseDateError(undefined);
    setFormError(undefined);
    setDraftRestored(false);
  };

  return (
    <div className="flex flex-col gap-5 pb-28">
      {isSubmitting && <LoadingOverlay message="Enregistrement du lot…" />}

      <div className="flex items-start gap-3">
        <BackButton onClick={onBack} />
        <PageHeader title="Approvisionner" subtitle="Enregistrez tout ce que vous avez reçu en une seule fois." />
      </div>

      {draftRestored ? (
        <div
          role="status"
          className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-brand/30 bg-brand-soft px-4 py-3 text-sm"
        >
          <p>
            <span className="font-semibold">Brouillon restauré.</span> Vos saisies sont conservées sur cet appareil.
          </p>
          <Button type="button" variant="ghost" size="sm" onClick={clearDraft}>
            Recommencer
          </Button>
        </div>
      ) : null}

      <FormErrorSummary
        fields={[
          {
            id: "provisioning-purchase-date",
            label: "Date d’achat",
            message: purchaseDateError,
          },
          ...lines.flatMap((line) => {
            const provision = provisions.find((item) => item.id === line.provisionId);
            const itemLabel = provision?.name ?? "la ligne sélectionnée";
            return [
              {
                id: `${line.key}-quantity`,
                label: `Quantité — ${itemLabel}`,
                message: lineErrors[line.key]?.quantity,
              },
              {
                id: `${line.key}-price`,
                label: `Prix — ${itemLabel}`,
                message: lineErrors[line.key]?.price,
              },
              {
                id: `${line.key}-expiry`,
                label: `Péremption — ${itemLabel}`,
                message: lineErrors[line.key]?.expiryDate,
              },
            ];
          }),
        ]}
      >
        {formError}
      </FormErrorSummary>

      {/* Set once for the whole batch instead of repeating it on every line. */}
      <div className="rounded-xl border border-brand/40 bg-brand-soft p-4">
        <p className="font-semibold">Valable pour tout le lot</p>
        <p className="mt-0.5 text-sm text-muted-foreground">Saisi une seule fois.</p>

        <div className="mt-4 grid gap-4 md:grid-cols-3">
          <FormField
            label="Date d’achat"
            htmlFor="provisioning-purchase-date"
            error={purchaseDateError}
            errorId="provisioning-purchase-date-error"
          >
            <input
              id="provisioning-purchase-date"
              type="date"
              value={purchaseDate}
              max={today}
              required
              aria-invalid={Boolean(purchaseDateError)}
              aria-describedby={purchaseDateError ? "provisioning-purchase-date-error" : undefined}
              onChange={(e) => {
                setPurchaseDate(e.target.value);
                setPurchaseDateError(undefined);
              }}
              className={FIELD_CLASS}
            />
          </FormField>

          <FormField label="Stock de destination" htmlFor="provisioning-stock">
            <input
              id="provisioning-stock"
              value={stock.name}
              readOnly
              className={cn(FIELD_CLASS, "text-muted-foreground")}
            />
          </FormField>

          <FormField label="Payé depuis" htmlFor="provisioning-account">
            {accounts.length > 1 ? (
              <SelectField
                id="provisioning-account"
                value={selectedAccount}
                onChange={setAccountId}
                aria-label="Compte qui a payé"
                options={accounts.map((account, index) => ({
                  value: account.id,
                  label: accountLabel(account, index),
                }))}
              />
            ) : (
              <input
                id="provisioning-account"
                value={accounts[0] ? accountLabel(accounts[0]) : "Aucun compte"}
                readOnly
                className={cn(FIELD_CLASS, "text-muted-foreground")}
              />
            )}
          </FormField>
        </div>
      </div>

      <FormField label="Libellé du lot" htmlFor="provisioning-label" hint="Facultatif — par exemple le fournisseur.">
        <Input
          id="provisioning-label"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="ex. Marché Castors — livraison du matin"
        />
      </FormField>

      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <div className="hidden grid-cols-[1fr_130px_150px_160px_44px] gap-3 bg-muted/60 px-4 py-3 text-[13px] font-semibold text-muted-foreground md:grid">
          <span>Matière première</span>
          <span>Quantité reçue</span>
          <span>Prix payé ({currencySymbol(currency)})</span>
          <span>Péremption</span>
          <span />
        </div>

        {lines.map((line, lineIndex) => {
          const provision = provisions.find((p) => p.id === line.provisionId);
          const lineLabel = provision?.name ?? `ligne ${lineIndex + 1}`;
          const quantityError = lineErrors[line.key]?.quantity;
          const priceError = lineErrors[line.key]?.price;
          const expiryError = lineErrors[line.key]?.expiryDate;
          return (
            <div
              key={line.key}
              className="grid gap-3 border-t border-muted px-4 py-3 md:grid-cols-[1fr_130px_150px_160px_44px] md:items-center"
            >
              <SelectField
                value={line.provisionId}
                onChange={(provisionId) => updateLine(line.key, { provisionId })}
                placeholder="Choisir une matière…"
                aria-label={`Matière première — ${lineLabel}`}
                options={provisions.map((item) => ({
                  value: item.id,
                  label: item.name,
                  hint: `en ${unitLabel(item.measurementUnit)}`,
                }))}
              />

              <div className="min-w-0">
                <NumericStepper
                  id={`${line.key}-quantity`}
                  min={0}
                  step={provision?.measurementPrecision ?? 1}
                  value={line.quantity}
                  onChange={(value) => updateLine(line.key, { quantity: String(value) })}
                  placeholder="0"
                  aria-label={`Quantité reçue — ${lineLabel}`}
                  aria-invalid={Boolean(quantityError)}
                  aria-describedby={quantityError ? `${line.key}-quantity-error` : undefined}
                  suffix={provision ? unitLabel(provision.measurementUnit) : undefined}
                />
                {quantityError ? (
                  <p id={`${line.key}-quantity-error`} className="sr-only">
                    {quantityError}
                  </p>
                ) : null}
              </div>

              <div className="min-w-0">
                <CurrencyInput
                  id={`${line.key}-price`}
                  value={line.price}
                  onValueChange={(value) => updateLine(line.key, { price: value })}
                  placeholder="Prix payé"
                  currency={currency}
                  aria-label={`Prix payé en ${currencySymbol(currency)} — ${lineLabel}`}
                  aria-invalid={Boolean(priceError)}
                  aria-describedby={priceError ? `${line.key}-price-error` : undefined}
                />
                {priceError ? (
                  <p id={`${line.key}-price-error`} className="sr-only">
                    {priceError}
                  </p>
                ) : null}
              </div>

              <div className="flex min-w-0 flex-col gap-1">
                <input
                  id={`${line.key}-expiry`}
                  type="date"
                  value={line.expiryDate}
                  min={today}
                  onChange={(e) => updateLine(line.key, { expiryDate: e.target.value })}
                  aria-label={`Date de péremption — ${lineLabel}`}
                  aria-invalid={Boolean(expiryError)}
                  aria-describedby={expiryError ? `${line.key}-expiry-error` : undefined}
                  className={FIELD_CLASS}
                />
                {expiryError ? (
                  <p id={`${line.key}-expiry-error`} className="sr-only">
                    {expiryError}
                  </p>
                ) : null}
              </div>

              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Retirer ${lineLabel}`}
                disabled={lines.length === 1}
                onClick={() => setLines((prev) => prev.filter((l) => l.key !== line.key))}
              >
                <Trash2 className="text-muted-foreground" />
              </Button>
            </div>
          );
        })}

        <div className="flex flex-wrap gap-2 border-t border-muted px-4 py-3">
          <button
            type="button"
            onClick={() => setLines((prev) => [...prev, emptyLine()])}
            className="flex min-h-11 flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2 text-[15px] font-medium transition hover:bg-muted"
          >
            <Plus size={18} />
            Ajouter une ligne
          </button>
          <button
            type="button"
            onClick={duplicateLastLine}
            className="flex min-h-11 flex-1 items-center justify-center gap-2 rounded-lg border border-input px-3 py-2 text-[15px] font-medium transition hover:bg-muted"
            aria-label="Dupliquer la dernière ligne sans sa quantité ni son prix"
          >
            <CopyPlus size={18} />
            Dupliquer la dernière
          </button>
        </div>
      </div>

      {/* The running total stays in view while the lines are being typed. */}
      <div className="fixed inset-x-0 bottom-16 z-20 border-t border-border bg-card px-4 py-3 md:bottom-0 md:left-64 md:px-8 md:py-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm text-muted-foreground">
              {filled.length} {filled.length > 1 ? "entrées" : "entrée"}
            </p>
            <p className="text-xl font-semibold tabular-nums">Total payé {formatMoney(total, currency)}</p>
          </div>
          <div className="flex gap-3">
            <Button type="button" variant="outline" size="lg" onClick={onBack}>
              Annuler
            </Button>
            <Button type="button" size="lg" onClick={handleSubmit} disabled={isSubmitting}>
              Enregistrer {filled.length > 0 ? `les ${filled.length} entrées` : "le lot"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
